/**
 * AppShell — eDMS Web Layout v2 (Phase 5.5 refresh)
 *
 * Two-pane chrome:
 *   - Sticky top-bar (dual-brand: QWise mark + tenant wordmark + global search +
 *     notifications + user menu + locale + theme).
 *   - Collapsible side-nav with 8 grouped domains.
 *   - Content area with breadcrumb header + children slot.
 *
 * Owner: Phase 5.5 — UX Polish & Dual-Brand Design.
 * URS rows: UX-001..005 (dual-brand chrome), UX-014..016 (Cmd+K), UX-019..020 (RTL).
 * Authoritative design spec: drafts/42_ux_polish_and_dual_brand_design.md §screen-by-screen.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouterState } from '@tanstack/react-router';
import { useSession } from '../../auth/SessionProvider';
import { CommandPalette, type PaletteEntry } from '../CommandPalette/CommandPalette';
import { SiteScopeSwitcher } from '../SiteScopeSwitcher';
import { DemoBanner } from '../DemoBanner';
import styles from './AppShell.module.css';

export interface AppShellProps {
  title: string;
  children: ReactNode;
}

type NavLeaf = {
  kind: 'leaf';
  to: string;
  labelKey: string;
  labelDefault: string;
  activePrefix?: string;
  /** Visible only if the user has at least one matching role.
   *  Missing/empty = visible to all authenticated users. (UX-015) */
  roles?: string[];
  /** Quick-action vs. destination. Action entries land in the palette's
   *  "Quick actions" section. */
  isAction?: boolean;
};

type NavGroup = {
  kind: 'group';
  id: string;
  labelKey: string;
  labelDefault: string;
  icon: string;
  children: NavLeaf[];
};

type NavItem = NavLeaf | NavGroup;

const NAV_ITEMS: NavItem[] = [
  { kind: 'leaf', to: '/', labelKey: 'nav.home', labelDefault: 'Home', activePrefix: '/' },
  { kind: 'leaf', to: '/action-center', labelKey: 'nav.actionCenter', labelDefault: 'Action Center', activePrefix: '/action-center' },
  { kind: 'leaf', to: '/search', labelKey: 'nav.globalSearch', labelDefault: 'Global Search', activePrefix: '/search' },

  {
    kind: 'group',
    id: 'documents',
    labelKey: 'nav.documents',
    labelDefault: 'Documents',
    icon: '📄',
    children: [
      { kind: 'leaf', to: '/documents/dashboard', labelKey: 'nav.documentDashboard', labelDefault: 'Document Dashboard', activePrefix: '/documents/dashboard' },
      { kind: 'leaf', to: '/documents/periodic-reviews', labelKey: 'nav.periodicReviews', labelDefault: 'Periodic Reviews', activePrefix: '/documents/periodic-reviews' },
      { kind: 'leaf', to: '/workbench',   labelKey: 'nav.workbench',  labelDefault: 'My Workbench',  activePrefix: '/workbench' },
      { kind: 'leaf', to: '/documents/search', labelKey: 'nav.documentSearch', labelDefault: 'Search', activePrefix: '/documents/search' },
      { kind: 'leaf', to: '/documents/corporate', labelKey: 'nav.corporateDocs', labelDefault: 'Corporate Documents', activePrefix: '/documents/corporate' },
      { kind: 'leaf', to: '/qa',         labelKey: 'nav.qa',         labelDefault: 'QA Workbench',
        roles: ['QAManager', 'QAApprover', 'QADirector'] },
      { kind: 'leaf', to: '/templates',   labelKey: 'nav.templates',  labelDefault: 'Template Library', activePrefix: '/templates' },
      { kind: 'leaf', to: '/ocr/review', labelKey: 'nav.ocrReview',  labelDefault: 'OCR Review',  activePrefix: '/ocr',
        roles: ['DocController', 'QAManager'] },
      { kind: 'leaf', to: '/documents/ocr-search', labelKey: 'nav.ocrSearch', labelDefault: 'OCR Search', activePrefix: '/documents/ocr-search' },
    ],
  },

  {
    kind: 'group',
    id: 'archive',
    labelKey: 'nav.archive',
    labelDefault: 'Archive & Records',
    icon: '🗄️',
    children: [
      { kind: 'leaf', to: '/archive', labelKey: 'nav.archiveDashboard', labelDefault: 'Archive Dashboard', activePrefix: '/archive' },
      { kind: 'leaf', to: '/archive/records', labelKey: 'nav.archiveRecords', labelDefault: 'Records', activePrefix: '/archive/records' },
    ],
  },

  {
    kind: 'group',
    id: 'quality',
    labelKey: 'nav.quality',
    labelDefault: 'Quality',
    icon: '📊',
    children: [
      { kind: 'leaf', to: '/dashboard/quality',          labelKey: 'nav.dashboardQuality',         labelDefault: 'QA Director Dashboard', activePrefix: '/dashboard/quality',
        roles: ['QADirector', 'QAManager', 'SiteQualityHead', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/quality-metrics',            labelKey: 'nav.qualityMetrics',           labelDefault: 'Metrics Dashboard',  activePrefix: '/quality-metrics',
        roles: ['QAManager', 'QADirector', 'QualityAnalyticsViewer', 'SiteQualityHead'] },
      { kind: 'leaf', to: '/quality-metrics/thresholds', labelKey: 'nav.qualityMetricsThresholds', labelDefault: 'Metric Thresholds',  activePrefix: '/quality-metrics/thresholds',
        roles: ['QAManager', 'QADirector'] },
      { kind: 'leaf', to: '/quality-metrics/snapshots',  labelKey: 'nav.qualityMetricsSnapshots',  labelDefault: 'Snapshot Signing',   activePrefix: '/quality-metrics/snapshots',
        roles: ['QADirector', 'QAManager'] },
      { kind: 'leaf', to: '/retention/matrix',           labelKey: 'nav.retentionMatrix',          labelDefault: 'Retention Matrix',   activePrefix: '/retention/matrix',
        roles: ['QAManager', 'DocController'] },
      { kind: 'leaf', to: '/retention/disposition',      labelKey: 'nav.retentionDisposition',     labelDefault: 'Disposition Tray',   activePrefix: '/retention/disposition',
        roles: ['QAManager', 'DocController'] },
    ],
  },

  {
    kind: 'group',
    id: 'suppliers',
    labelKey: 'nav.suppliers',
    labelDefault: 'Suppliers',
    icon: '🏭',
    children: [
      { kind: 'leaf', to: '/suppliers/avl',           labelKey: 'nav.suppliersAvl',       labelDefault: 'Approved Vendor List', activePrefix: '/suppliers/avl',
        roles: ['ProcurementOfficer', 'SupplierManager', 'QAManager', 'QAApprover', 'QADirector', 'AuditorInternal'] },
      { kind: 'leaf', to: '/suppliers/audit-findings', labelKey: 'nav.suppliersFindings',  labelDefault: 'Audit Findings', activePrefix: '/suppliers/audit-findings',
        roles: ['ProcurementOfficer', 'SupplierManager', 'QAManager', 'QAApprover', 'QADirector', 'AuditorInternal'] },
      { kind: 'leaf', to: '/suppliers/change-notifications', labelKey: 'nav.suppliersScn', labelDefault: 'Change Notifications', activePrefix: '/suppliers/change-notifications',
        roles: ['ProcurementOfficer', 'SupplierManager', 'QAManager', 'QAApprover', 'QADirector', 'AuditorInternal'] },
      { kind: 'leaf', to: '/scars', labelKey: 'nav.scars', labelDefault: 'Corrective Actions (SCAR)', activePrefix: '/scars' },
      { kind: 'leaf', to: '/suppliers',               labelKey: 'nav.suppliersWorkbench', labelDefault: 'Workbench',
        roles: ['ProcurementOfficer', 'SupplierManager', 'QAManager', 'QAApprover', 'AuditorInternal'] },
      { kind: 'leaf', to: '/suppliers/risk-ranking',  labelKey: 'nav.suppliersRisk',      labelDefault: 'Risk Ranking',  activePrefix: '/suppliers/risk-ranking',
        roles: ['QAManager', 'QADirector', 'ProcurementOfficer'] },
      { kind: 'leaf', to: '/suppliers/kpi-dashboard', labelKey: 'nav.suppliersKpi',       labelDefault: 'KPI Dashboard', activePrefix: '/suppliers/kpi-dashboard',
        roles: ['QAManager', 'QADirector', 'SupplierManager'] },
    ],
  },

  {
    kind: 'group',
    id: 'equipment',
    labelKey: 'nav.equipment',
    labelDefault: 'Equipment',
    icon: '⚙️',
    children: [
      { kind: 'leaf', to: '/equipment',                          labelKey: 'nav.equipmentWorkbench',      labelDefault: 'Workbench',
        roles: ['ValidationLead', 'QAManager', 'CalibrationTechnician', 'MaintenanceEngineer'] },
      { kind: 'leaf', to: '/equipment/calibrations',             labelKey: 'nav.equipmentCalibrations',   labelDefault: 'Calibrations',          activePrefix: '/equipment/calibrations',
        roles: ['CalibrationTechnician', 'ValidationLead', 'QAManager'] },
      { kind: 'leaf', to: '/equipment/pm',                       labelKey: 'nav.equipmentPM',             labelDefault: 'Preventive Maintenance', activePrefix: '/equipment/pm',
        roles: ['MaintenanceEngineer', 'ValidationLead', 'QAManager'] },
      { kind: 'leaf', to: '/equipment/requalification-schedule', labelKey: 'nav.equipmentRequalification', labelDefault: 'Requalification',       activePrefix: '/equipment/requalification-schedule',
        roles: ['ValidationLead', 'QAManager'] },
    ],
  },

  {
    kind: 'group',
    id: 'production',
    labelKey: 'nav.production',
    labelDefault: 'Production',
    icon: '⚗️',
    children: [
      { kind: 'leaf', to: '/production', labelKey: 'nav.productionMaster', labelDefault: 'Master Data & Artwork', activePrefix: '/production' },
    ],
  },

  {
    kind: 'group',
    id: 'quality-events',
    labelKey: 'nav.qualityEvents',
    labelDefault: 'Quality Events',
    icon: '🛡️',
    children: [
      { kind: 'leaf', to: '/change-control',        labelKey: 'nav.changeControl',     labelDefault: 'Change Control',      activePrefix: '/change-control',
        roles: ['QAManager', 'QAApprover', 'QADirector', 'Author', 'ValidationLead', 'ProcurementOfficer'] },
      { kind: 'leaf', to: '/change-control/dashboard', labelKey: 'nav.ccDashboard',    labelDefault: 'CC Dashboard',        activePrefix: '/change-control/dashboard',
        roles: ['QAManager', 'QAApprover', 'QADirector', 'Author', 'ValidationLead', 'ProcurementOfficer'] },
      { kind: 'leaf', to: '/capa',                 labelKey: 'nav.capa',              labelDefault: 'CAPA',                activePrefix: '/capa',
        roles: ['QAManager', 'QAApprover', 'QADirector', 'CapaOwner', 'CapaApprover'] },
      { kind: 'leaf', to: '/deviations',           labelKey: 'nav.deviations',        labelDefault: 'Deviations',          activePrefix: '/deviations',
        roles: ['QAManager', 'QADirector', 'DeviationOwner', 'Investigator'] },
      { kind: 'leaf', to: '/oos',                  labelKey: 'nav.oos',               labelDefault: 'Out-of-Spec',         activePrefix: '/oos',
        roles: ['QAManager', 'LabAnalyst', 'QCLead', 'Investigator'] },
      { kind: 'leaf', to: '/risks',                labelKey: 'nav.risks',             labelDefault: 'Risk Register',       activePrefix: '/risks',
        roles: ['QAManager', 'QADirector', 'RiskOwner'] },
      { kind: 'leaf', to: '/complaints',           labelKey: 'nav.complaints',        labelDefault: 'Complaints',          activePrefix: '/complaints',
        roles: ['QAManager', 'QADirector', 'ComplaintHandler', 'Investigator'] },
      { kind: 'leaf', to: '/recalls',              labelKey: 'nav.recalls',           labelDefault: 'Recalls',             activePrefix: '/recalls',
        roles: ['QAManager', 'QADirector', 'RecallCoordinator'] },
      { kind: 'leaf', to: '/validation',           labelKey: 'nav.validation',        labelDefault: 'Validation',          activePrefix: '/validation',
        roles: ['QAManager', 'QADirector', 'ValidationEngineer', 'QualityEngineer'] },
      { kind: 'leaf', to: '/internal-audits',      labelKey: 'nav.internalAudits',    labelDefault: 'Internal Audits',     activePrefix: '/internal-audits',
        roles: ['QAManager', 'QADirector', 'AuditorInternal'] },
      { kind: 'leaf', to: '/management-reviews',   labelKey: 'nav.managementReviews', labelDefault: 'Management Review',   activePrefix: '/management-reviews',
        roles: ['QADirector', 'ExecutiveSponsor', 'QAManager'] },
    ],
  },

  {
    kind: 'group',
    id: 'regulatory',
    labelKey: 'nav.regulatory',
    labelDefault: 'Regulatory',
    icon: '🏛️',
    children: [
      { kind: 'leaf', to: '/regulatory/dashboard', labelKey: 'nav.raDashboard',        labelDefault: 'RA Dashboard',        activePrefix: '/regulatory/dashboard',
        roles: ['QAManager', 'QADirector', 'RAManager', 'RAOfficer'] },
      { kind: 'leaf', to: '/regulatory/products',  labelKey: 'nav.productRegister',    labelDefault: 'Product Register',    activePrefix: '/regulatory/products',
        roles: ['QAManager', 'QADirector', 'RAManager', 'RAOfficer'] },
      { kind: 'leaf', to: '/regulatory/registrations', labelKey: 'nav.registrationTracker', labelDefault: 'Registration Tracker', activePrefix: '/regulatory/registrations',
        roles: ['QAManager', 'QADirector', 'RAManager', 'RAOfficer'] },
      { kind: 'leaf', to: '/regulatory/variations', labelKey: 'nav.variations', labelDefault: 'Variations', activePrefix: '/regulatory/variations',
        roles: ['QAManager', 'QADirector', 'RAManager', 'RAOfficer'] },
      { kind: 'leaf', to: '/regulatory/renewals', labelKey: 'nav.renewals', labelDefault: 'Renewals', activePrefix: '/regulatory/renewals',
        roles: ['QAManager', 'QADirector', 'RAManager', 'RAOfficer'] },
      { kind: 'leaf', to: '/regulatory/market-matrix', labelKey: 'nav.marketMatrix', labelDefault: 'Country / Market Matrix', activePrefix: '/regulatory/market-matrix',
        roles: ['QAManager', 'QADirector', 'RAManager', 'RAOfficer'] },
      { kind: 'leaf', to: '/regulatory/commitments', labelKey: 'nav.commitments', labelDefault: 'Commitments', activePrefix: '/regulatory/commitments',
        roles: ['QAManager', 'QADirector', 'RAManager', 'RAOfficer'] },
    ],
  },

  {
    kind: 'group',
    id: 'pharmacovigilance',
    labelKey: 'nav.pharmacovigilance',
    labelDefault: 'Pharmacovigilance',
    icon: '💊',
    children: [
      { kind: 'leaf', to: '/pharmacovigilance', labelKey: 'nav.pvRegisters', labelDefault: 'Registers', activePrefix: '/pharmacovigilance' },
    ],
  },

  {
    kind: 'group',
    id: 'finance',
    labelKey: 'nav.finance',
    labelDefault: 'Finance',
    icon: '💼',
    children: [
      { kind: 'leaf', to: '/finance/executive',       labelKey: 'nav.financeExecutive',     labelDefault: 'Executive',        activePrefix: '/finance/executive',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/finance/board-metrics',    labelKey: 'nav.financeBoardMetrics',  labelDefault: 'CEO / Board',      activePrefix: '/finance/board-metrics',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/finance/working-capital',  labelKey: 'nav.financeWorkingCapital', labelDefault: 'Working Capital', activePrefix: '/finance/working-capital',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/finance/treasury',         labelKey: 'nav.financeTreasury',      labelDefault: 'Treasury',         activePrefix: '/finance/treasury',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/finance/profitability',    labelKey: 'nav.financeProfitability', labelDefault: 'Profitability',    activePrefix: '/finance/profitability',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/finance/pharma-kpis',      labelKey: 'nav.financePharmaKpis',    labelDefault: 'Pharma KPIs',      activePrefix: '/finance/pharma-kpis',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/finance/forecasting',      labelKey: 'nav.financeForecasting',   labelDefault: 'Forecasting',      activePrefix: '/finance/forecasting',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/finance/uploads',          labelKey: 'nav.financeUploads',       labelDefault: 'Data Uploads',     activePrefix: '/finance/uploads',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
    ],
  },

  {
    kind: 'group',
    id: 'cost-of-quality',
    labelKey: 'nav.costOfQuality',
    labelDefault: 'Cost of Quality',
    icon: '💰',
    children: [
      { kind: 'leaf', to: '/cost-of-quality',           labelKey: 'nav.financeCoq',         labelDefault: 'Cost of Quality',  activePrefix: '/cost-of-quality',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/cockpit',   labelKey: 'nav.financeCockpit',     labelDefault: 'Quality Finance',  activePrefix: '/cost-of-quality/cockpit',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/cost-attribution', labelKey: 'nav.financeEntries', labelDefault: 'Cost Entries',  activePrefix: '/cost-of-quality/cost-attribution',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/cost-capture', labelKey: 'nav.financeCostCapture', labelDefault: 'Auto-capture', activePrefix: '/cost-of-quality/cost-capture',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/exposure-register', labelKey: 'nav.financeExposure', labelDefault: 'Exposure',  activePrefix: '/cost-of-quality/exposure-register',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/budgets',   labelKey: 'nav.financeBudgets',     labelDefault: 'Budgets',          activePrefix: '/cost-of-quality/budgets',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/variance',  labelKey: 'nav.financeVariance',    labelDefault: 'Variance',         activePrefix: '/cost-of-quality/variance',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/capex',     labelKey: 'nav.financeCapex',       labelDefault: 'CAPEX',            activePrefix: '/cost-of-quality/capex',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/capa-roi',  labelKey: 'nav.financeCapaRoi',     labelDefault: 'CAPA ROI',         activePrefix: '/cost-of-quality/capa-roi',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/erp-reconciliation', labelKey: 'nav.financeErpRecon', labelDefault: 'ERP Recon', activePrefix: '/cost-of-quality/erp-reconciliation',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/period-close', labelKey: 'nav.financePeriodClose', labelDefault: 'Period Close', activePrefix: '/cost-of-quality/period-close',
        roles: ['FinanceReader', 'FinanceEditor', 'CFO', 'ExecutiveSponsor'] },
      { kind: 'leaf', to: '/cost-of-quality/board-pack', labelKey: 'nav.financeBoardPack',  labelDefault: 'Board Pack',       activePrefix: '/cost-of-quality/board-pack',
        roles: ['CFO', 'ExecutiveSponsor', 'QADirector'] },
      { kind: 'leaf', to: '/cost-of-quality/board-analytics', labelKey: 'nav.financeBoardAnalytics', labelDefault: 'Board Analytics', activePrefix: '/cost-of-quality/board-analytics',
        roles: ['CFO', 'ExecutiveSponsor', 'QADirector'] },
      { kind: 'leaf', to: '/cost-of-quality/admin/cost-rates', labelKey: 'nav.financeAdminRates', labelDefault: 'Cost Rates', activePrefix: '/cost-of-quality/admin/cost-rates',
        roles: ['CFO', 'FinanceEditor'] },
      { kind: 'leaf', to: '/cost-of-quality/admin/period-locks', labelKey: 'nav.financeAdminLocks', labelDefault: 'Period Locks', activePrefix: '/cost-of-quality/admin/period-locks',
        roles: ['CFO', 'FinanceEditor'] },
      { kind: 'leaf', to: '/cost-of-quality/admin/erp', labelKey: 'nav.financeAdminErp',    labelDefault: 'ERP Systems',      activePrefix: '/cost-of-quality/admin/erp',
        roles: ['CFO', 'FinanceEditor'] },
      { kind: 'leaf', to: '/cost-of-quality/admin/audit', labelKey: 'nav.financeAudit',     labelDefault: 'Audit Log',        activePrefix: '/cost-of-quality/admin/audit',
        roles: ['CFO', 'FinanceEditor', 'FinanceReader'] },
    ],
  },

  { kind: 'leaf', to: '/training',         labelKey: 'nav.training',    labelDefault: 'Training',  activePrefix: '/training' },
  { kind: 'leaf', to: '/inspector/audit',  labelKey: 'nav.auditTrail', labelDefault: 'Audit Trail', activePrefix: '/inspector',
    roles: ['Inspector', 'QAManager', 'QADirector', 'AuditorInternal', 'TenantAdmin'] },

  {
    kind: 'group',
    id: 'admin',
    labelKey: 'nav.admin',
    labelDefault: 'Admin',
    icon: '🛠️',
    children: [
      { kind: 'leaf', to: '/admin/sites',       labelKey: 'nav.sites',       labelDefault: 'Sites',        activePrefix: '/admin/sites',
        roles: ['TenantAdmin', 'QADirector', 'QAManager'] },
      { kind: 'leaf', to: '/admin/departments', labelKey: 'nav.departments', labelDefault: 'Departments', activePrefix: '/admin/departments',
        roles: ['TenantAdmin', 'QADirector', 'QAManager'] },
      { kind: 'leaf', to: '/admin/tenant-config', labelKey: 'nav.tenantConfig',  labelDefault: 'Tenant Config', activePrefix: '/admin/tenant-config',
        roles: ['TenantAdmin', 'QADirector'] },
      { kind: 'leaf', to: '/admin/numbering', labelKey: 'nav.documentNumbering', labelDefault: 'Document Numbering', activePrefix: '/admin/numbering',
        roles: ['TenantAdmin', 'QADirector', 'QAManager'] },
      { kind: 'leaf', to: '/admin/data-scenarios', labelKey: 'nav.dataScenarios', labelDefault: 'Data Scenarios', activePrefix: '/admin/data-scenarios',
        roles: ['TenantAdmin', 'QADirector', 'QAManager'] },
      { kind: 'leaf', to: '/admin/webhooks',      labelKey: 'nav.webhooks',      labelDefault: 'Webhooks',      activePrefix: '/admin/webhooks',
        roles: ['TenantAdmin', 'SystemAdmin'] },
      { kind: 'leaf', to: '/notifications',       labelKey: 'nav.notifications', labelDefault: 'Notifications', activePrefix: '/notifications' },
    ],
  },
];

/** Super-user roles see/enter every module regardless of per-leaf gates. */
export const SUPER_USER_ROLES = ['TenantAdmin', 'SystemAdmin', 'Admin'];
export function isSuperUser(userRoles: string[] | undefined | null): boolean {
  return Array.isArray(userRoles) && userRoles.some((r) => SUPER_USER_ROLES.includes(r));
}

/** True if the leaf is visible to a user holding `userRoles`. */
function leafVisibleToUser(leaf: NavLeaf, userRoles: string[]): boolean {
  if (isSuperUser(userRoles)) return true;            // tenant/system admin → full nav
  if (!leaf.roles || leaf.roles.length === 0) return true;
  if (userRoles.length === 0) return false;          // unauthenticated → hide gated leaves
  const userSet = new Set(userRoles);
  return leaf.roles.some((r) => userSet.has(r));
}

/** Filter NAV_ITEMS by the user's roles (UX-015). Groups with no visible
 *  children are dropped. */
function filterNavByRoles(items: NavItem[], userRoles: string[]): NavItem[] {
  const out: NavItem[] = [];
  for (const item of items) {
    if (item.kind === 'leaf') {
      if (leafVisibleToUser(item, userRoles)) out.push(item);
    } else {
      const children = item.children.filter((c) => leafVisibleToUser(c, userRoles));
      if (children.length > 0) out.push({ ...item, children });
    }
  }
  return out;
}

export function AppShell({ title, children }: AppShellProps) {
  const { t, i18n } = useTranslation();
  const sessionCtx = useOptionalSession();
  // useOptionalSession returns the context object {session, loading, login, logout, setSession}.
  // Components want the inner Session (userId / preferredUsername / roles / etc.), so unwrap here.
  const session = sessionCtx?.session ?? null;
  const routerState = useOptionalRouterState();
  const currentPath = routerState?.location.pathname ?? '/';

  // UX-015: filter NAV_ITEMS by the user's roles.
  const userRoles = session?.roles ?? [];
  const visibleNavItems = useMemo(
    () => filterNavByRoles(NAV_ITEMS, userRoles),
    [userRoles],
  );

  // Exactly one nav leaf is "active" — the longest activePrefix matching the
  // current path (drafts/59 REV-4). Drives aria-current + group expansion.
  const activeLeafTo = useMemo(
    () => activeLeafToFor(visibleNavItems, currentPath),
    [visibleNavItems, currentPath],
  );

  // UX-014: Cmd+K palette state.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // UX-014/016: build palette entries from the filtered nav tree + register the
  // global Cmd+K / Ctrl+K keydown handler.
  const paletteEntries = useMemo<PaletteEntry[]>(() => {
    const out: PaletteEntry[] = [];
    for (const item of visibleNavItems) {
      if (item.kind === 'leaf') {
        out.push({
          id: item.to,
          label: t(item.labelKey, { defaultValue: item.labelDefault }),
          hint: item.to,
          to: item.to,
          icon: '→',
          section: item.isAction ? 'quickActions' : 'pages',
        });
      } else {
        for (const child of item.children) {
          out.push({
            id: child.to,
            label: t(child.labelKey, { defaultValue: child.labelDefault }),
            hint: `${t(item.labelKey, { defaultValue: item.labelDefault })} · ${child.to}`,
            to: child.to,
            icon: item.icon,
            section: child.isAction ? 'quickActions' : 'pages',
          });
        }
      }
    }
    return out;
  }, [visibleNavItems, t]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const [sideNavOpen, setSideNavOpen] = useState(false);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const item of visibleNavItems) {
      if (item.kind === 'group') {
        const hit = item.children.some((c) => c.to === activeLeafTo);
        if (hit) initial.add(item.id);
      }
    }
    return initial;
  });

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      for (const item of visibleNavItems) {
        if (item.kind === 'group') {
          const hit = item.children.some((c) => c.to === activeLeafTo);
          if (hit) next.add(item.id);
        }
      }
      return next;
    });
  }, [currentPath, visibleNavItems]);

  function toggleGroup(id: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleLocale() {
    const next = i18n.language.startsWith('ar') ? 'en-US' : 'ar-SA';
    i18n.changeLanguage(next);
  }

  function toggleTheme() {
    const current = document.documentElement.getAttribute('data-theme') ?? 'light';
    const next = current === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('edms_theme', next); } catch { /* ignore */ }
  }

  /**
   * Compute up-to-2-char initials from a username/display name.
   * Examples:
   *   audit-bot           → AB
   *   mohanad.ali         → MA
   *   Hossam A.           → HA
   *   single              → SI (first 2 letters when only one token)
   *   (nothing)           → ··
   */
  function computeInitials(name?: string | null): string {
    if (!name) return '··';
    const cleaned = name.replace(/[._@\-]+/g, ' ').trim();
    const parts = cleaned.split(/\s+/).filter(Boolean);
    const [first, second] = parts;
    if (first && second) {
      return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase();
    }
    if (first) {
      return first.slice(0, 2).toUpperCase();
    }
    return '··';
  }

  function onLogout() {
    if (sessionCtx) {
      sessionCtx.logout();
    } else {
      try {
        for (const key of [
          'edms.session.access', 'edms.session.refresh', 'edms.session.expires',
          'edms.session.userId', 'edms.session.tenantId', 'edms.session.preferredUsername',
          'edms.session.roles',
        ]) sessionStorage.removeItem(key);
      } catch { /* ignore */ }
      window.location.assign('/login');
    }
  }

  const breadcrumb = useMemo(() => buildBreadcrumb(currentPath, t), [currentPath, t]);

  return (
    <div className={styles.shell}>
      <DemoBanner />
      <SystemBanner />

      <header className={styles.topBar} role="banner">
        <div className={styles.topBarLeft}>
          <button
            type="button"
            className={styles.hamburger}
            onClick={() => setSideNavOpen((v) => !v)}
            aria-label={sideNavOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={sideNavOpen}
            aria-controls="primary-side-nav"
          >
            <span aria-hidden="true">{sideNavOpen ? '✕' : '☰'}</span>
          </button>

          <Link to="/" className={styles.brandLockup} aria-label={title}>
            <span className={styles.qwiseMark} aria-hidden="true">
              <svg viewBox="0 0 28 28" width="22" height="22" focusable="false">
                <defs>
                  <linearGradient id="qwShellGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1DD1A1"/>
                    <stop offset="55%" stopColor="#22D3EE"/>
                    <stop offset="100%" stopColor="#0A84FF"/>
                  </linearGradient>
                </defs>
                <circle cx="12.5" cy="12.5" r="10" stroke="url(#qwShellGrad)" strokeWidth="2.5" fill="none"/>
                <rect x="8"  y="10" width="2" height="2" fill="#22D3EE"/>
                <rect x="11" y="12" width="2" height="2" fill="#0A84FF"/>
                <rect x="8"  y="14" width="2" height="2" fill="#0A84FF"/>
                <rect x="11" y="16" width="2" height="2" fill="#22D3EE"/>
                <path d="M16 17 L24 25 L21 25 L14 18 Z" fill="url(#qwShellGrad)"/>
              </svg>
            </span>
            <span className={styles.qwiseWord} aria-hidden="true">QWise</span>
            <span className={styles.brandDivider} aria-hidden="true">/</span>
            <img
              src="/brand/spectropharma-wordmark.png"
              alt=""
              className={styles.tenantLogo}
              width={132}
              height={22}
              decoding="async"
            />
            <span className={styles.brandSr}>{title}</span>
          </Link>

          <div className={styles.siteScopeSlot}>
            <SiteScopeSwitcher />
          </div>
        </div>

        <div className={styles.topBarCenter}>
          <button
            type="button"
            className={styles.commandTrigger}
            onClick={openPalette}
            aria-label="Open command palette (Ctrl+K)"
            aria-haspopup="dialog"
            aria-expanded={paletteOpen}
          >
            <span className={styles.commandIcon} aria-hidden="true">⌕</span>
            <span className={styles.commandLabel}>{t('nav.search', { defaultValue: 'Search or jump to…' })}</span>
            <kbd className={styles.commandKbd} aria-hidden="true">⌘K</kbd>
          </button>
        </div>

        <div className={styles.topBarRight}>
          {/* Desktop-only icons; on mobile these collapse into the side nav (CSS .topBarIconsDesktop hides them <720px) */}
          <div className={styles.topBarIconsDesktop}>
            <button type="button" className={styles.iconButton} onClick={toggleLocale} aria-label="Toggle language" title="EN / AR">
              {i18n.language.startsWith('ar') ? 'EN' : 'ع'}
            </button>
            <button type="button" className={styles.iconButton} onClick={toggleTheme} aria-label="Toggle theme" title="Dark / Light">
              🌓
            </button>
            <Link to="/notifications" className={styles.iconButton} aria-label="Notifications">🔔</Link>
          </div>
          <div className={styles.userMenu} aria-label="User menu">
            <span aria-hidden="true" className={styles.avatar} title={session?.preferredUsername || session?.userId || ''}>
              {computeInitials(session?.preferredUsername)}
            </span>
            {session?.userId && (
              <button type="button" className={styles.logoutButton} onClick={onLogout}
                aria-label={t('auth.logout', { defaultValue: 'Sign out' })}>
                {t('auth.logout', { defaultValue: 'Sign out' })}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className={styles.body}>
        <aside
          id="primary-side-nav"
          className={`${styles.sideNav} ${sideNavOpen ? styles.sideNavOpenMobile : ''}`}
          aria-label="Primary navigation"
        >
          <nav className={styles.navList}>
            {visibleNavItems.map((item) => {
              if (item.kind === 'leaf') {
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    // exact:true stops TanStack's default fuzzy match from
                    // auto-setting aria-current on parent/sibling prefixes
                    // (the double-highlight bug); our activeLeafTo drives the
                    // intended highlight. (drafts/59 REV-4.)
                    activeOptions={{ exact: true }}
                    className={`${styles.navLeaf} ${styles.navLeafTop}`}
                    aria-current={item.to === activeLeafTo ? 'page' : undefined}
                    onClick={() => setSideNavOpen(false)}
                  >
                    <span className={styles.navLabel}>{t(item.labelKey, { defaultValue: item.labelDefault })}</span>
                  </Link>
                );
              }
              const expanded = expandedGroups.has(item.id);
              const groupActive = item.children.some((c) => c.to === activeLeafTo);
              return (
                <div key={item.id} className={`${styles.navGroup} ${groupActive ? styles.navGroupActive : ''}`}>
                  <button
                    type="button"
                    className={styles.navGroupHeader}
                    onClick={() => toggleGroup(item.id)}
                    aria-expanded={expanded}
                    aria-controls={`navgrp-${item.id}`}
                  >
                    <span className={styles.navGroupIcon} aria-hidden="true">{item.icon}</span>
                    <span className={styles.navLabel}>{t(item.labelKey, { defaultValue: item.labelDefault })}</span>
                    <span className={`${styles.navChevron} ${expanded ? styles.navChevronOpen : ''}`} aria-hidden="true">›</span>
                  </button>
                  {expanded && (
                    <div id={`navgrp-${item.id}`} className={styles.navGroupChildren}>
                      {item.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          activeOptions={{ exact: true }}
                          className={styles.navLeaf}
                          aria-current={child.to === activeLeafTo ? 'page' : undefined}
                          onClick={() => setSideNavOpen(false)}
                        >
                          <span className={styles.navLabel}>{t(child.labelKey, { defaultValue: child.labelDefault })}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          {/* Mobile-only utility row at the bottom of the side nav.
              Surfaces language / theme / notifications / logout when the top
              bar has collapsed them away on narrow viewports. */}
          <div className={styles.sideNavUtilityMobile}>
            <button type="button" className={styles.navLeaf} onClick={toggleLocale}>
              <span aria-hidden="true">{i18n.language.startsWith('ar') ? '🌐' : '🌐'}</span>
              <span className={styles.navLabel}>
                {i18n.language.startsWith('ar') ? 'English' : 'العربية'}
              </span>
            </button>
            <button type="button" className={styles.navLeaf} onClick={toggleTheme}>
              <span aria-hidden="true">🌓</span>
              <span className={styles.navLabel}>
                {t('nav.toggleTheme', { defaultValue: 'Toggle theme' })}
              </span>
            </button>
            <Link to="/notifications" className={styles.navLeaf} onClick={() => setSideNavOpen(false)}>
              <span aria-hidden="true">🔔</span>
              <span className={styles.navLabel}>
                {t('nav.notifications', { defaultValue: 'Notifications' })}
              </span>
            </Link>
            {session?.userId && (
              <button type="button" className={styles.navLeafLogout} onClick={onLogout}>
                <span aria-hidden="true">⏏</span>
                <span className={styles.navLabel}>
                  {t('auth.logout', { defaultValue: 'Sign out' })}
                </span>
              </button>
            )}
          </div>
        </aside>

        <main className={styles.content}>
          {breadcrumb.length > 0 && (
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              {breadcrumb.map((seg, idx) => (
                <span key={idx} className={styles.breadcrumbItem}>
                  {idx > 0 && <span className={styles.breadcrumbSep} aria-hidden="true">/</span>}
                  {seg.to ? (
                    // exact:true so an intermediate crumb (a parent path) doesn't
                    // pick up TanStack's fuzzy aria-current — only the final crumb
                    // below is the current page. (drafts/59 REV-4 a11y follow-up.)
                    <Link to={seg.to} activeOptions={{ exact: true }} className={styles.breadcrumbLink}>{seg.label}</Link>
                  ) : (
                    <span className={styles.breadcrumbCurrent} aria-current="page">{seg.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
          <div className={styles.contentInner}>{children}</div>
        </main>
      </div>

      {/* Cmd+K command palette (UX-014..016) */}
      <CommandPalette
        open={paletteOpen}
        onClose={closePalette}
        entries={paletteEntries}
      />
    </div>
  );
}

function leafMatchesPath(leaf: NavLeaf, currentPath: string): boolean {
  const prefix = leaf.activePrefix ?? leaf.to;
  if (prefix === '/') return currentPath === '/';
  return currentPath === prefix || currentPath.startsWith(prefix + '/');
}

// Returns the `to` of the single nav leaf that best matches the current path —
// the one with the LONGEST matching activePrefix. Without this, a module's
// short prefix (e.g. `/suppliers`) prefix-matches every sub-page
// (`/suppliers/kpi-dashboard`), so the workbench leaf stays highlighted
// alongside the real sub-item. Longest-prefix-wins → exactly one active leaf.
// (drafts/59 REV-4.)
function activeLeafToFor(items: NavItem[], currentPath: string): string | null {
  let bestTo: string | null = null;
  let bestLen = -1;
  const consider = (leaf: NavLeaf) => {
    if (!leafMatchesPath(leaf, currentPath)) return;
    const prefix = leaf.activePrefix ?? leaf.to;
    if (prefix.length > bestLen) {
      bestLen = prefix.length;
      bestTo = leaf.to;
    }
  };
  for (const it of items) {
    if (it.kind === 'leaf') consider(it);
    else it.children.forEach(consider);
  }
  return bestTo;
}

function buildBreadcrumb(
  currentPath: string,
  t: (k: string, opts?: { defaultValue?: string }) => string,
): Array<{ label: string; to?: string }> {
  if (currentPath === '/') return [];
  const segments = currentPath.split('/').filter(Boolean);
  const out: Array<{ label: string; to?: string }> = [];
  out.push({ label: t('nav.home', { defaultValue: 'Home' }), to: '/' });
  let accum = '';
  for (let i = 0; i < segments.length; i++) {
    accum += '/' + segments[i];
    const isLast = i === segments.length - 1;
    const label = labelForPath(accum, t);
    if (isLast) out.push({ label });
    else out.push({ label, to: accum });
  }
  return out;
}

function labelForPath(path: string, t: (k: string, opts?: { defaultValue?: string }) => string): string {
  for (const item of NAV_ITEMS) {
    if (item.kind === 'leaf') {
      if (item.to === path) return t(item.labelKey, { defaultValue: item.labelDefault });
    } else {
      for (const child of item.children) {
        if (child.to === path) return t(child.labelKey, { defaultValue: child.labelDefault });
      }
    }
  }
  const last = path.split('/').filter(Boolean).pop() ?? '';
  return last.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function useOptionalSession() {
  try { return useSession(); } catch { return null; }
}

function useOptionalRouterState() {
  try { return useRouterState(); } catch { return null; }
}

function SystemBanner() { return null; }
